import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import GameLayout from '../../../components/escape-to-inner-peace/layout/GameLayout';
import ScoreTimerBar from '../../../components/escape-to-inner-peace/game-shell/ScoreTimerBar';
import RoomScene from '../../../components/escape-to-inner-peace/game-shell/RoomScene';
import StoryPopup from '../../../components/escape-to-inner-peace/overlays/StoryPopup';
import LearningPopup from '../../../components/escape-to-inner-peace/overlays/LearningPopup';
import GamePopup from '../../../components/escape-to-inner-peace/overlays/GamePopup';
import '../../../components/escape-to-inner-peace/overlays/TextPopup.css';
import ActivityOneStatements from '../../../components/escape-to-inner-peace/activities/ActivityOneStatements';
import ActivityTwoWordInputs from '../../../components/escape-to-inner-peace/activities/ActivityTwoWordInputs';
import ActivityThreeQuestion from '../../../components/escape-to-inner-peace/activities/ActivityThreeQuestion';
import ActivityFourWordCloud from '../../../components/escape-to-inner-peace/activities/ActivityFourWordCloud';
import ActivityFiveWordPuzzle from '../../../components/escape-to-inner-peace/activities/ActivityFiveWordPuzzle';
import { useEscapeTimer } from '../../../games/escape-to-inner-peace/hooks/useEscapeTimer';
import { useEscapeScore } from '../../../games/escape-to-inner-peace/hooks/useEscapeScore';
import { useEscapeProgress } from '../../../games/escape-to-inner-peace/hooks/useEscapeProgress';
import { useEscapePersistence } from '../../../games/escape-to-inner-peace/hooks/useEscapePersistence';
import { CLUE_TEXT, LEARNING_TEXT } from '../../../games/escape-to-inner-peace/data/gameText';
import './game.css';

const ACTIVITY_COMPONENTS = {
  mirror: ActivityOneStatements,
  book: ActivityTwoWordInputs,
  tablet: ActivityThreeQuestion,
  safe: ActivityFourWordCloud,
  chest: ActivityFiveWordPuzzle,
};

const PERIODIC_SAVE_INTERVAL_MS = 20000;

function GamePage() {
  const persistence = useEscapePersistence();

  if (persistence.status === 'completed') {
    return (
      <Navigate
        to="/escape-to-inner-peace/result"
        replace
        state={{
          score: persistence.completedResult.totalScore,
          seconds: persistence.completedResult.elapsedSeconds,
          gameRunId: persistence.gameRunId,
        }}
      />
    );
  }

  if (persistence.status === 'loading') {
    return (
      <GameLayout>
        <p className="escape-persistence-status" data-testid="escape-persistence-loading">
          Loading your game...
        </p>
      </GameLayout>
    );
  }

  if (persistence.status === 'error') {
    return (
      <GameLayout>
        <div className="escape-persistence-status escape-persistence-error" data-testid="escape-persistence-error">
          <p>{persistence.error}</p>
          <button type="button" className="escape-begin-btn" onClick={persistence.retry} data-testid="escape-persistence-retry">
            Retry
          </button>
        </div>
      </GameLayout>
    );
  }

  return <GameRoom persistence={persistence} />;
}

function GameRoom({ persistence }) {
  const navigate = useNavigate();
  const { initialState, gameRunId, saveProgress, completeGame, saveError } = persistence;
  const [isRunning, setIsRunning] = useState(true);
  const seconds = useEscapeTimer(isRunning, initialState.elapsedSeconds);
  const score = useEscapeScore(initialState.activityScores);

  const secondsRef = useRef(seconds);
  const scoreTotalRef = useRef(score.total);
  useEffect(() => {
    secondsRef.current = seconds;
  }, [seconds]);
  useEffect(() => {
    scoreTotalRef.current = score.total;
  }, [score.total]);

  const [completionState, setCompletionState] = useState({ status: 'idle', error: null });
  const isCompletingRef = useRef(false);
  const isRunningRef = useRef(isRunning);

  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  const attemptComplete = async () => {
    if (isCompletingRef.current) return;
    isCompletingRef.current = true;
    setCompletionState({ status: 'pending', error: null });

    let confirmed = null;
    try {
      const result = await completeGame({
        elapsedSeconds: secondsRef.current,
        totalScore: scoreTotalRef.current,
      });
      if (result?.success && result?.state?.status === 'completed') {
        confirmed = result.state;
      }
    } catch {
      confirmed = null;
    }

    if (confirmed) {
      navigate('/escape-to-inner-peace/result', {
        state: {
          score: confirmed.totalScore,
          seconds: confirmed.elapsedSeconds,
          gameRunId,
        },
      });
      return;
    }

    isCompletingRef.current = false;
    setCompletionState({
      status: 'error',
      error: 'We could not save your final result. Please retry.',
    });
  };

  const handleGameComplete = () => {
    setIsRunning(false);
    attemptComplete();
  };

  const progress = useEscapeProgress({
    onGameComplete: handleGameComplete,
    gameRunId,
    initialUnlockedIndex: initialState.unlockedIndex,
    initialCompleted: initialState.completed,
    skipInitialPopup: initialState.isResumed,
  });

  const handleActivityComplete = (id, points) => {
    const previousPoints = score.activityScores[id] || 0;
    const nextTotalScore = score.total + Math.max(0, points - previousPoints);
    score.setActivityScore(id, points);
    progress.completeActivity(id);
    saveProgress({
      activityId: id,
      activityScore: points,
      elapsedSeconds: secondsRef.current,
      totalScore: nextTotalScore,
    });
  };

  useEffect(() => {
    if (!isRunning || completionState.status === 'pending') return undefined;
    const interval = setInterval(() => {
      if (!isRunningRef.current || isCompletingRef.current) return;
      saveProgress({ elapsedSeconds: secondsRef.current, totalScore: scoreTotalRef.current });
    }, PERIODIC_SAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [saveProgress, isRunning, completionState.status]);

  useEffect(() => {
    const saveOnHide = () => {
      if (!isRunning || isCompletingRef.current || completionState.status === 'pending') return;
      if (document.visibilityState === 'hidden') {
        saveProgress(
          { elapsedSeconds: secondsRef.current, totalScore: scoreTotalRef.current },
          { keepalive: true }
        );
      }
    };
    document.addEventListener('visibilitychange', saveOnHide);
    window.addEventListener('pagehide', saveOnHide);
    return () => {
      document.removeEventListener('visibilitychange', saveOnHide);
      window.removeEventListener('pagehide', saveOnHide);
    };
  }, [saveProgress, isRunning, completionState.status]);

  const activePopup = progress.activePopup;
  const ActiveActivityComponent =
    activePopup?.type === 'activity' ? ACTIVITY_COMPONENTS[activePopup.id] : null;

  return (
    <GameLayout>
      {saveError && (
        <p className="escape-persistence-banner" data-testid="escape-save-error-banner">
          Progress couldn&apos;t be saved - will retry automatically.
        </p>
      )}
      <ScoreTimerBar seconds={seconds} points={score.total} />
      <RoomScene
        onObjectClick={progress.openActivity}
        isUnlocked={progress.isUnlocked}
        isCompleted={progress.isCompleted}
      />

      <StoryPopup
        open={activePopup?.type === 'initial'}
        onClose={progress.closeInitialPopup}
        text={CLUE_TEXT.mirror}
        testId="escape-initial-clue-popup"
      />

      {ActiveActivityComponent && (
        <ActiveActivityComponent
          open
          onComplete={(points) => handleActivityComplete(activePopup.id, points)}
        />
      )}

      <LearningPopup
        open={activePopup?.type === 'learning'}
        onClose={progress.closeLearningPopup}
        text={activePopup?.type === 'learning' ? LEARNING_TEXT[activePopup.id] : ''}
        testId="escape-learning-popup"
      />

      <StoryPopup
        open={activePopup?.type === 'clue'}
        onClose={progress.closeCluePopup}
        text={activePopup?.type === 'clue' ? CLUE_TEXT[activePopup.id] : ''}
        testId="escape-clue-popup"
      />

      <GamePopup
        open={completionState.status === 'error'}
        onClose={undefined}
        size="small"
        ariaLabel="Could not save your result"
        closeButton={false}
        testId="escape-completion-status"
      >
        <p className="escape-popup-text">{completionState.error}</p>
        <button
          type="button"
          className="escape-begin-btn"
          onClick={attemptComplete}
          disabled={completionState.status === 'pending'}
          data-testid="escape-completion-retry"
        >
          Retry
        </button>
      </GamePopup>
    </GameLayout>
  );
}

export default GamePage;
